import {
  WcgChipType,
  WcgMenuOption,
  WcgToastConfig,
  WcgToastPanelSize,
} from '@wcg/frontend-framework';
import {
  ERM_ICONS_ENUM,
  ExpansionPanelType,
  LoaderTableConfig,
} from '@erm/cluster';

import {
  DashboardPinnedContainerItem,
  DashboardLayout,
  DashboardPinnedItemsSignalState,
  OnYouActionBadge,
  WAITING_ON_YOU_SIGNAL_STATE,
  OutcomeDocumentStage,
  OutcomeReadyRequestParams,
  OutcomeReadySignalStateType,
  ConfigMapper,
  ConfigPanelStateKey,
  DashboardConfigComponentType,
  PICK_UP_SIGNAL_STATE,
  WAITING_ON_YOU_SIGNAL_STATE_V2,
  WaitingOnYouActionBadgeV2,
  WaitingOnYouReviewItemStatus,
  DashboardWaitingOnYouRequestParamsV2,
} from '../types';

export enum DASHBOARD_LAYOUT_KEYS_ENUM {
  WAITING_ON_YOU = 'waiting_on_you',
  WAITING_ON_YOU_V2 = 'waiting_on_you_v2',
  IN_WCG_HANDS = 'in_wcgs_hands',
  PICK_UP_WHERE_YOU_LEFT_OFF = 'pick_up_where_you_left_off',
  OUTCOMES_READY = 'outcomes_ready',
  SUBMISSION_STATUS = 'submissions_in_progress',
  PINNED_STUDIES_AND_SITES = 'pinned_studies_and_sites',
}

export const DEFAULT_DASHBOARD_LAYOUT: DashboardLayout[] = [
  {
    key: DASHBOARD_LAYOUT_KEYS_ENUM.WAITING_ON_YOU,
    title: 'Waiting on you',
    description: 'To-dos, questions, and items needing your response',
    icon: ERM_ICONS_ENUM.REPORT_PROBLEM,
    ordinal: 1,
    visible: true,
  },
  {
    key: DASHBOARD_LAYOUT_KEYS_ENUM.OUTCOMES_READY,
    title: 'Outcomes ready',
    description: 'Recent approval letters, conditionals, and review notes',
    icon: ERM_ICONS_ENUM.CELEBRATION,
    ordinal: 2,
    visible: true,
  },
  {
    key: DASHBOARD_LAYOUT_KEYS_ENUM.SUBMISSION_STATUS,
    title: 'Submission status',
    description: 'Across all active submissions by stage',
    icon: ERM_ICONS_ENUM.WORK_OUTLINE,
    ordinal: 3,
    visible: true,
  },
  {
    key: DASHBOARD_LAYOUT_KEYS_ENUM.PINNED_STUDIES_AND_SITES,
    title: 'My Pinned Items',
    description: 'Grid view of all pinned studies and sites',
    icon: ERM_ICONS_ENUM.PUSH_PIN_TRANSPARENT,
    ordinal: 4,
    visible: true,
  },
  {
    key: DASHBOARD_LAYOUT_KEYS_ENUM.PICK_UP_WHERE_YOU_LEFT_OFF,
    title: 'Pick up where you left off',
    description: 'Last three draft submissions you were working on',
    icon: ERM_ICONS_ENUM.PUSH_PIN_TRANSPARENT,
    ordinal: 5,
    visible: true,
  },
];

/**
 * Pristine rows for the floating dashboard config panel — the four data-driven
 * cards, each in data mode with the mock flag off. Seed and reset target for
 * the dashboard page's config signal.
 */
export const DEFAULT_DASHBOARD_CONFIG_PANELS: ConfigMapper = new Map([
  [
    'waiting-on-you',
    {
      componentType: 'waiting-on-you',
      name: 'Waiting on you',
      currentState: 'toggleMock',
      toggleMock: false,
    },
  ],
  [
    'outcome-ready',
    {
      componentType: 'outcome-ready',
      name: 'Outcomes ready',
      currentState: 'toggleMock',
      toggleMock: false,
    },
  ],
  [
    'submission-status',
    {
      componentType: 'submission-status',
      name: 'Submission status',
      currentState: 'toggleMock',
      toggleMock: false,
    },
  ],
  [
    'pinned-studies-sites',
    {
      componentType: 'pinned-studies-sites',
      name: 'My Pinned Items',
      currentState: 'toggleMock',
      toggleMock: false,
    },
  ],
  [
    'pick-up-where-you-left-off',
    {
      componentType: 'pick-up-where-you-left-off',
      name: 'Pick up where you left off',
      currentState: 'toggleMock',
      toggleMock: false,
    },
  ],
]);

/**
 * Simulated latency for mock-served dashboard card loads, in milliseconds.
 *
 * Kept non-zero so a card flipped to mock data still exercises its loading
 * state instead of snapping straight to rendered rows.
 */
export const DASHBOARD_MOCK_DELAY_MS = 300;

export const DEFAULT_SUBMISSION_CHART_GRID = {
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
  containLabel: false,
};

export const DEFAULT_DONUT_TEXT_STYLE = {
  fill: '#333333',
  align: 'center' as const,
};

export const DEFAULT_DONUT_SUBTITLE_STYLE = {
  fill: '#808080',
  align: 'center' as const,
};

export const DASHBOARD_SUBMISSION_STATUS_COLORS = new Map([
  ['Draft', '#F59E0B'],
  ['Pre-Review', '#9A2FC9'],
  ['Review', '#00B6EF'],
  ['Complete', '#01DAB0'],
  ['Default', '#DDE1E4'],
]);

export const DASHBOARD_SUBMISSION_STATUS_ENUM = {
  Draft: 'Draft',
  PreReview: 'Pre-Review',
  Review: 'Review',
  Complete: 'Complete',
};

export const DEFAULT_DASHBOARD_PINNED_CONTAINER_ITEMS: DashboardPinnedContainerItem[] =
  [
    {
      id: `study`,
      label: `studies`,
      icon: ERM_ICONS_ENUM.VACCINES,
    },
    {
      id: `site`,
      label: `sites`,
      icon: ERM_ICONS_ENUM.PLACE,
    },
  ];

/**
 * Maximum number of pinned items requested in the initial `GET /dashboard/pinned/`
 * call, for both the `study` and `site` entity types.
 *
 * A user can pin at most 100 items, so a single request of this size returns the
 * complete set. Fetching everything up front is what allows the pinned carousel to
 * advance past the first row of cards, and it keeps `count === items.length` — the
 * invariant the pin/unpin state update in `PinnedStudiesSitesSignalState` relies on
 * when it recalculates `count` from the loaded items.
 *
 * @example
 * ```ts
 * pinnedState.getPinnedItems({
 *   requestParams: {
 *     entity_type: 'site',
 *     limit: PINNED_ITEMS_MAX_LIMIT,
 *     offset: 0,
 *     sort_by: 'NEWEST',
 *   },
 * });
 * ```
 */
export const PINNED_ITEMS_MAX_LIMIT = 100;

export const DEFAULT_DASHBOARD_PINNED_ITEMS_SIGNAL_STATE: DashboardPinnedItemsSignalState =
  {
    error: null,
    isLoading: false,
    isUpdatingPinnedState: false,
    data: null,
    isPinInitiated: false,
    lastToggledEntityId: undefined,
  };

export const PINNED_CHIP_MAPPER = new Map<string, WcgChipType>([
  ['draft', WcgChipType.WARNING],
  ['pre-review', WcgChipType.INFO],
  ['review', WcgChipType.INFO],
  ['complete', WcgChipType.SUCCESS],
  ['approved', WcgChipType.SUCCESS],
  ['pending', WcgChipType.INFO],
  ['to-do', WcgChipType.TODO],
  ['closed', WcgChipType.CRITICAL],
]);

//'draft' | 'pre-review' | 'review' | 'complete' | 'to-do' | 'pending'
export const PINNED_CHIP_LABEL_MAPPER = new Map<string, string>([
  ['draft', 'draft'],
  ['review', 'in review'],
  ['pre-review', 'pre review'],
  ['complete', 'Complete'],
  ['to-do', 'to do'],
]);

export const SUBMISSION_STATUS_LABEL_MAPPER = new Map<string, string>([
  ['draft', 'Draft'],
  ['pre-review', 'Pre-review'],
  ['Pre-Review', 'Pre-review'],
  ['review', 'Review'],
  ['complete', 'Complete'],
  ['to-do', 'To do'],
  ['pending', 'Pending'],
]);

export const PINNED_ICON_MAPPER = new Map<string, string>([
  ['source_tracking_id', ERM_ICONS_ENUM.VPN_KEY],
  ['sponsor_protocol_id', ERM_ICONS_ENUM.ASSIGNMENT_IND],
  ['study_name', ERM_ICONS_ENUM.VACCINES],
  ['sponsor_name', ERM_ICONS_ENUM.BUSINESS],
  ['number_of_sites', ERM_ICONS_ENUM.PLACE],
]);

export const PINNED_CHIP_STYLE_MAPPER = new Map<string, string[]>([
  ['draft', []],
]);

/**
 * Icon for each mockable state fab in the floating dashboard config panel.
 *
 * Insertion order is render order: the panel draws one fab per entry, per
 * dashboard card. The empty-state and error-state icons match the ones
 * `wcg-empty-state-vertical` renders inside the cards.
 */
export const FLOAT_CONFIG_PANEL_ICON_MAPPER = new Map<
  ConfigPanelStateKey,
  ERM_ICONS_ENUM
>([
  ['emptyState', ERM_ICONS_ENUM.HOURGLASS_EMPTY],
  ['loadingState', ERM_ICONS_ENUM.CIRCLE_CIRCLE],
  ['errorState', ERM_ICONS_ENUM.REPORT_PROBLEM],
  ['toggleMock', ERM_ICONS_ENUM.TOGGLE_ON],
]);

/**
 * Icon for each card group's accordion header in the floating dashboard
 * config panel — the same icon the card's own dashboard header uses (see
 * {@link DEFAULT_DASHBOARD_LAYOUT}).
 */
export const FLOAT_CONFIG_PANEL_GROUP_ICON_MAPPER = new Map<
  DashboardConfigComponentType,
  ERM_ICONS_ENUM
>([
  ['waiting-on-you', ERM_ICONS_ENUM.REPORT_PROBLEM],
  ['outcome-ready', ERM_ICONS_ENUM.CELEBRATION],
  ['submission-status', ERM_ICONS_ENUM.SUBMISSION_STATUS],
  ['pinned-studies-sites', ERM_ICONS_ENUM.PUSH_PIN_TRANSPARENT],
  ['pick-up-where-you-left-off', ERM_ICONS_ENUM.EDIT_PENCIL],
]);

/**
 * Loader-table skeleton configuration for the legacy Submission Status dashboard card.
 *
 * - 3 columns: Status (with circle prefix), Count, % Done
 * - 4 body rows
 * - Footer row for totals
 * - Column 1 footer uses a wider skeleton (70%) to mirror "Total stages" label width
 */
export const SUBMISSION_STATUS_LOADER_TABLE_CONFIG: LoaderTableConfig = {
  rows: 4,
  showFooter: true,
  columns: [
    { width: '100%', height: '14px', hasCirclePrefix: true },
    { width: '50%', height: '14px' },
    { width: '50%', height: '14px' },
  ],
  headerWidth: '60%',
  headerHeight: '14px',
};

/**
 * Loader-table skeleton config for the feature-flagged Submission Status card.
 *
 * Keeps the new design compact: three rows, no header, and no footer.
 */
export const SUBMISSION_IN_PROGRESS_LOADER_TABLE_CONFIG: LoaderTableConfig = {
  rows: 3,
  showHeader: false,
  showFooter: false,
  columns: [
    {
      width: '100%',
      height: '1.2rem',
      padding: '3rem 0 2.5rem',
      hasCirclePrefix: true,
    },
    {
      width: '22%',
      height: '1.2rem',
      padding: '3rem 0 2.5rem',
    },
    {
      width: '16%',
      height: '1.2rem',
      padding: '3rem 0 2.5rem',
    },
  ],
};

export const PINNED_SORT_ORDER_OPTIONS: WcgMenuOption[] = [
  { id: 'NEWEST', value: 'NEWEST', label: 'Sort: Newest' },
  { id: 'OLDEST', value: 'OLDEST', label: 'Sort: Oldest' },
  { id: 'NAME_ASC', value: 'NAME_ASC', label: 'Sort: A to Z' },
  { id: 'NAME_DESC', value: 'NAME_DESC', label: 'Sort: Z to A' },
];

export const ON_YOU_ACTION_BADGES: OnYouActionBadge[] = [
  {
    id: 'all',
    label: 'All',
    actionCount: null,
    showBadge: true,
    actionBadgeClass: 'on-you__action-badge',
    viewAllLabel: 'View submissions needing your action',
  },
  {
    id: 'todo',
    label: 'To do',
    actionCount: 0,
    showBadge: true,
    actionBadgeClass: 'on-you__action-badge',
    viewAllLabel: 'View all To Do submissions',
  },
  {
    id: 'on_hold',
    label: 'On hold',
    actionCount: 0,
    showBadge: true,
    actionBadgeClass: 'on-you__action-badge',
    viewAllLabel: 'View all On Hold submissions',
  },
];

/**
 * @NOTE: WAITING ON YOU DEFAULT SIGNAL STATE
 */
export const DEFAULT_WAITING_ON_PROPS = {
  error: null,
  isLoading: false,
};
export const WAITING_ON_YOU_DEFAULT_SIGNAL_STATE: WAITING_ON_YOU_SIGNAL_STATE =
  {
    ...DEFAULT_WAITING_ON_PROPS,
    data: null,
  };

//'data' | 'todoAccess' | 'outcomeAccess' | 'seeDetailsAccess'
export enum WAITING_ON_YOU_V2_ERROR_TYPE_ENUM {
  'DATA' = 'data',
  'TODO_ACCESS' = 'todoAccess',
  'OUTCOME_ACCESS' = 'outcomeAccess',
  'SEE_DETAILS_ACCESS' = 'seeDetailsAccess',
}

export const WAITING_ON_YOU_V2_DEFAULT_SIGNAL_STATE: WAITING_ON_YOU_SIGNAL_STATE_V2 =
  {
    ...DEFAULT_WAITING_ON_PROPS,
    data: null,
    checkOutcomeAccessResponse: null,
    checkTodoAccessResponse: null,
    checkSeeDetailsAccessResponse: null,
    checkOutcomeAccessResponseLoading: false,
    checkTodoAccessResponseLoading: false,
    checkSeeDetailsAccessResponseLoading: false,
  };

export const determineErrorType = (
  type: WAITING_ON_YOU_V2_ERROR_TYPE_ENUM,
  error: Error | null,
) => {
  return {
    type,
    error,
  };
};

export const DASH_BOARD_SCROLL_MAX_LIMIT = 10;

/**
 * @NOTE: PICK UP WHERE YOU LEFT OFF DEFAULT SIGNAL STATE
 */
export const PICK_UP_DEFAULT_SIGNAL_STATE: PICK_UP_SIGNAL_STATE = {
  error: null,
  isLoading: false,
  data: null,
};
/**
 * @NOTE: ON YOU ACTION BADGE ENUM
 */
export const ON_YOU_ACTION_BADGES_ENUM = {
  todo: 'To do',
  on_hold: 'On hold',
  all: 'All',
};

/** Filter badges for the redesigned "Waiting on you" card (`waiting_on_you` flag on). */
export const WAITING_ON_YOU_V2_ACTION_BADGES: WaitingOnYouActionBadgeV2[] = [
  { id: 'all', label: 'All' },
  { id: 'study', label: 'Study level' },
  { id: 'site', label: 'Site level' },
];

/**
 * @NOTE: OUTCOMES READY
 */

/**
 * Default query parameters for the Outcomes Ready request.
 *
 * `limit` is pinned to the API's maximum (100) so a single request returns the
 * largest page the endpoint allows; `offset` starts at the first item.
 */
export const OUTCOME_READY_DEFAULT_REQUEST_PARAMS: OutcomeReadyRequestParams = {
  limit: 100,
  offset: 0,
};

/**
 * Pristine state for the "Outcomes ready" dashboard card. Used as the seed for
 * `OUTCOME_READY_SIGNAL_STATE` and as the target of `resetState()`.
 */
export const OUTCOME_READY_DEFAULT_SIGNAL_STATE: OutcomeReadySignalStateType = {
  error: null,
  isLoading: false,
  data: null,
  expandedWorkspaceId: null,
  downloadingIds: [],
  queuedIds: [],
  downloadProgress: {},
  groupProgress: {},
  groupPhases: {},
};

/**
 * How long a submission group's success/failure indicator stays on its header
 * before clearing itself.
 *
 * Matches the 2000ms `saved` dwell in `wcg-progress-helper` so the Outcomes
 * Ready card and the pinned-items card feel like the same product.
 */
export const OUTCOME_READY_GROUP_PHASE_RESET_MS = 2000;

/**
 * Document count above which a submission-group download is delivered as a
 * single zip archive instead of individual file saves.
 *
 * A group of one or two documents downloads as individual files; three or more
 * are zipped, matching the platform's standard multi-document behaviour and
 * avoiding a burst of browser save prompts.
 */
export const OUTCOME_READY_ZIP_THRESHOLD = 2;

/**
 * Timeframe caption rendered in the "Outcomes ready" card header. The API only
 * returns documents posted inside this window, so the label is static.
 */
export const OUTCOME_READY_TIMEFRAME_LABEL = 'Last 10 days';

/**
 * Maps a document's section to the shared expansion-panel visual vocabulary.
 *
 * `ExpansionPanelType` belongs to `@erm/cluster` and is used across the product,
 * so the Outcomes Ready stages are translated at the boundary rather than the
 * shared type being reshaped around this one card.
 */
export const OUTCOME_READY_STAGE_PANEL_TYPE_MAPPER: Readonly<
  Record<OutcomeDocumentStage, ExpansionPanelType>
> = {
  completed: 'success',
  individual: 'default',
};

/**
 * Maps a document's section to the icon rendered on its tile.
 *
 * @remarks This is deliberately **not** the "document type icon" the acceptance
 * criteria call for — see ERM-11985 / ERM-12931. That requires an icon per
 * document type from design, and it is still open. Until it lands, a row's icon
 * is a constant determined solely by which section it sits in.
 */
export const OUTCOME_READY_DOC_ICON_MAPPER: Readonly<
  Record<OutcomeDocumentStage, ERM_ICONS_ENUM>
> = {
  completed: ERM_ICONS_ENUM.PDF_SUCCESS,
  individual: ERM_ICONS_ENUM.PDF_REVIEW,
};

/**
 * Contractual toast copy for every "Outcomes ready" download outcome.
 *
 * @remarks These strings are approved product copy — do not reword them.
 */
export const OUTCOME_READY_TOAST = {
  /**
   * Copy for a successful single-document download.
   *
   * @param name - Title of the downloaded document.
   * @returns The success toast message.
   */
  documentSuccess: (name: string): string => `${name} has downloaded.`,
  /**
   * Copy for a fully successful submission-group download.
   *
   * @param count - Number of documents that downloaded successfully.
   * @param submissionName - Name of the submission the documents belong to.
   * @returns The success toast message.
   */
  groupSuccess: (count: number, submissionName: string): string =>
    `Download complete. ${count} ${count === 1 ? 'document' : 'documents'} from ${submissionName} ${count === 1 ? 'is' : 'are'} downloaded.`,
  /** Copy for a document the user can no longer access (401/403/404). */
  noLongerAvailable:
    'This document is no longer available. You may no longer have access to it.',
  /** Copy for any non-access download failure. */
  generalFailure: "We couldn't complete your download. Please try again.",
  /**
   * Copy for a submission-group download where **no** document succeeded.
   *
   * @remarks ERM-12931 §3 flagged that the AC supplies no group-level failure
   * string. This names the submission so a user with several groups open knows
   * which one failed, rather than seeing the anonymous per-document copy.
   *
   * @param submissionName - Name of the submission whose download failed.
   * @returns The failure toast message.
   */
  groupFailure: (submissionName: string): string =>
    `We couldn't download the documents from ${submissionName}. Please try again.`,
  /**
   * Copy for a partially successful submission-group download.
   *
   * @param count - Number of documents that could not be downloaded.
   * @returns The warning toast message.
   */
  partial: (count: number): string =>
    `Some documents were downloaded. ${count} document(s) were not accessible and could not be included.`,
  /**
   * Copy for a wholly failed group download where only *some* of the failures
   * were lost-access.
   *
   * @remarks ERM-13269: `partial` cannot be reused for this case — its leading
   * "Some documents were downloaded" is false when none were, and "could not be
   * included" presumes an archive that was never saved. The retry invitation is
   * kept because the *other* failures here are transient and may well succeed.
   *
   * @remarks Shaped after `groupFailure` — "We couldn't download … from
   * {submission}. Please try again." — with the count spliced in. An earlier
   * revision led with the count and put the submission mid-sentence
   * (`"1 document(s) from {name} were not accessible…"`), which reads badly
   * once a real submission name is substituted: the name separates the subject
   * from its verb by thirty-odd characters. Keeping the name in the trailing
   * prepositional phrase, exactly where `groupFailure` puts it, keeps the two
   * failure messages parsing the same way.
   *
   * @remarks Not yet approved product copy. Isolated in this one constant so a
   * reword is a one-line change.
   *
   * @param count - Number of documents the user can no longer access.
   * @param submissionName - Name of the submission whose download failed.
   * @returns The mixed-failure toast message.
   */
  mixedFailure: (count: number, submissionName: string): string =>
    `We couldn't download ${count} document(s) from ${submissionName}. Please try again.`,
} as const;

/**
 * Snackbar panel classes used by the "Outcomes ready" toasts. Values match the
 * classes `FileService.showSnackbar` is already called with elsewhere.
 */
export const OUTCOME_READY_SNACKBAR_CLASS: Readonly<
  Record<'success' | 'error' | 'warning', string>
> = {
  success: 'snackbar-success',
  error: 'snackbar-error',
  warning: 'snackbar-warning',
};

/**
 * Percentage the elapsed-time download estimate climbs to and stops at.
 *
 * @remarks Set to a full 100 by product decision. The estimate has no
 * knowledge of the real transfer size, so a download still in flight after
 * {@link OUTCOME_READY_PROGRESS_DURATION_MS} shows a completed-looking ring
 * until it actually settles. That trade was made deliberately in favour of a
 * ring that always resolves rather than one that visibly stalls short of full.
 */
export const OUTCOME_READY_PROGRESS_CEILING = 100;

/**
 * Time the elapsed-time download estimate takes to travel from 0 to
 * {@link OUTCOME_READY_PROGRESS_CEILING}, in milliseconds.
 *
 * Tuned short so the ring reads as responsive on the fast downloads that make
 * up the common case.
 */
export const OUTCOME_READY_PROGRESS_DURATION_MS = 1_200;

/**
 * How often the elapsed-time download estimate is recomputed, in milliseconds.
 *
 * Kept well under the frame budget so a sweep this short still animates
 * smoothly rather than stepping visibly.
 */
export const OUTCOME_READY_PROGRESS_TICK_MS = 50;

/**
 * To-do `todo_entity_type` values that carry no questionnaire-section context.
 *
 * Item cards for these to-dos must not render the "Section # of #" fragment
 * (nor the separating dot) on the Status line — only questionnaire to-dos
 * (`question`) reference a section within the submission. Mirrors the Waiting
 * on You AC: on-hold cards, document review requests, and additional document
 * requests from SME/QC never show section progress.
 */
export const DASHBOARD_NO_SECTION_ENTITY_TYPES: ReadonlySet<string> = new Set([
  'qc_mark_required',
  'document_conditionally_required',
  'document_type_required',
  'document_type_requested',
  'sme_mark_required',
  'document',
]);

export const DASHBOARD_PINNED_STUDIES_HOME_PATH = '/studies/home';
export const DASHBOARD_PINNED_SITES_HOME_PATH = '/sites/home';

export const WAITING_ON_YOU_ON_HOLD_STATUS_COLOR_MAPPER = new Map<
  WaitingOnYouReviewItemStatus,
  string
>([
  ['on_hold', 'bg-(--color-amber-orange)!'],
  ['in_progress', 'bg-(--color-royal-blue)!'],
  ['complete', 'bg-(--color-aqua-teal)!'],
  ['draft', 'bg-(--color-bluegrey-12)!'],
]);

export const WAITING_ON_HOLD_PANEL_STATUS_MAPPER = new Map<
  WaitingOnYouReviewItemStatus,
  string
>([
  ['on_hold', 'On hold'],
  ['in_progress', 'In progress'],
  ['complete', 'Complete · View outcome documents →'],
  ['draft', 'Draft'],
]);

export const WAITING_ON_YOU_DESCRIPTION_MAPPER = new Map<string, string>([
  ['todo', 'The WCG Team needs a response to continue.'],
  ['on_hold', 'The WCG Team needs a response to resume the review.'],
  [
    'tod_ready_to_resubmit',
    'All to-dos are complete. Resubmit so the WCG Team can continue.',
  ],
]);

export const DEFAULT_WAITING_ON_REQUEST_PARAMS: DashboardWaitingOnYouRequestParamsV2 =
  {
    filter: 'all',
    page: 1,
    page_size: 10,
  };

export const waitingOnYouAccessibilityCheck = (
  message: string,
): WcgToastConfig => ({
  data: {
    message: message,
  },
  duration: 5000,
  toastType: 'info',
  width: WcgToastPanelSize.XXXLarge,
  height: WcgToastPanelSize.XXLarge,
  horizontalPosition: 'center',
  icon: ERM_ICONS_ENUM.BELL,
  iconPosition: 'end',
});

export const DEFAULT_V2_SUBMISSION_URL = '/v2/submissions/';
